/**
 * Profile API — manages extended user profile data.
 *
 * Profile data is stored separately from auth/UserRecord to keep
 * sensitive auth data isolated.
 *
 * KV key:  co:__user:profile:{username}  ->  UserProfile object
 *
 * The profile supplements the UserRecord (from userApi.ts) with
 * optional fields like avatar, email, phone, bio, and preferences.
 *
 * SAVE: Uses the dedicated PUT /profiles/:username endpoint which
 * handles its own auth and returns profile-specific error messages.
 * This avoids the generic /content/bulk path and its auth issues.
 */

import { apiFetch } from "./apiClient";
import { getSessionToken } from "./userApi";

export interface UserPreferences {
  darkMode?: boolean;
  notifications?: boolean;
  language?: "he" | "en";
}

export interface UserProfile {
  username: string;
  email?: string;
  phone?: string;
  avatarUrl?: string;
  bio?: string;
  lastLogin?: number;
  loginCount?: number;
  preferences?: UserPreferences;
  updatedAt: number;
}

/* ================================================================
   In-memory cache for profiles (batch fetch)
   ================================================================ */
let _profilesCache: Record<string, UserProfile> | null = null;
let _profilesCacheTime = 0;
const PROFILES_CACHE_TTL = 30_000; // 30 seconds

/** Invalidate the profiles cache (call after mutations) */
export function invalidateProfilesCache(): void {
  _profilesCache = null;
  _profilesCacheTime = 0;
}

// --- Key helpers ---
const PROFILE_PREFIX = "__user:profile:";

function profileKey(username: string): string {
  return PROFILE_PREFIX + username.toLowerCase();
}

// --- Public API ---

/** Get a single user's profile. */
export async function getProfile(username: string): Promise<UserProfile | null> {
  try {
    const data = await apiFetch("/profiles");
    const raw: Record<string, any> = data.content || {};
    const key = profileKey(username);
    const value = raw[key];
    if (value && typeof value === "object") return value as UserProfile;
    return null;
  } catch {
    return null;
  }
}

/**
 * Save / update a user's profile.
 *
 * Uses the dedicated PUT /profiles/:username endpoint which:
 *   - Handles its own auth check with specific error messages
 *   - Only allows users to edit their own profile (or admins)
 *   - Returns the merged profile object
 *
 * If the session is expired, throws an error with a clear message
 * instead of a generic 401.
 */
export async function saveProfile(
  username: string,
  updates: Partial<Omit<UserProfile, "username" | "updatedAt">>,
): Promise<UserProfile> {
  // Pre-check: is there a session token at all?
  const token = getSessionToken();
  if (!token) {
    throw new Error("SESSION_EXPIRED: Please log in again to save your profile");
  }

  const data = await apiFetch(`/profiles/${encodeURIComponent(username.toLowerCase())}`, {
    method: "PUT",
    body: JSON.stringify({ profile: updates }),
  });

  invalidateProfilesCache();
  return data.profile as UserProfile;
}

/** Record a login timestamp (non-blocking helper). */
export async function recordLogin(username: string): Promise<void> {
  try {
    const existing = await getProfile(username);
    const count = (existing?.loginCount || 0) + 1;
    await saveProfile(username, {
      lastLogin: Date.now(),
      loginCount: count,
    });
  } catch {
    // Non-critical — silently ignore
  }
}

/** Get all profiles in one batch call. Uses dedicated /profiles endpoint. */
export async function getAllProfiles(): Promise<Record<string, UserProfile>> {
  // Return cached data if fresh
  if (_profilesCache && (Date.now() - _profilesCacheTime) < PROFILES_CACHE_TTL) {
    return _profilesCache;
  }

  try {
    const data = await apiFetch("/profiles");
    const raw: Record<string, any> = data.content || {};
    const profiles: Record<string, UserProfile> = {};

    for (const [key, value] of Object.entries(raw)) {
      if (key.startsWith(PROFILE_PREFIX) && value && typeof value === "object") {
        const username = key.slice(PROFILE_PREFIX.length);
        profiles[username] = value as UserProfile;
      }
    }

    _profilesCache = profiles;
    _profilesCacheTime = Date.now();
    return profiles;
  } catch {
    return {};
  }
}

/**
 * Resize an image File to a square avatar and return as base64 data URL.
 * Uses canvas for client-side resizing.
 */
export function resizeAvatarToBase64(file: File, size: number = 120): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Failed to load image"));
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("Canvas not supported")); return; }

        // Center-crop to square
        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);

        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}